import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { generateTimeline } from "../../../util/timelineHelper";
import * as recipeService from "@beers/services/recipeService";
import { SESSION_STATES } from "@common/constants/sessionConstants";
import mqttService from "../../mqttService";
import TimelineStage from "./TimelineStage";
import "./BrewTimeline.scss";

const createFallbackRecipe = (session) => {
  const setpoint =
    (session && session.device && session.device.setpoint_temp) ||
    (session && session.target_temp) ||
    18.0;

  // Compute realistic fallback duration based on session age if available
  let durationDays = 7;
  if (session && session.created) {
    const elapsedDays =
      (Date.now() - new Date(session.created).getTime()) /
      (1000 * 60 * 60 * 24);
    if (elapsedDays > 0) {
      durationDays = Math.max(7, Math.ceil(elapsedDays + 3));
    }
  }

  return {
    beer_name:
      (session && session.beer && session.beer.name) || "Custom Fermentation",
    serving_temperature: (session && session.serving_temperature) || 5.0,
    fermenting: [
      {
        fermentation_stage_type: "PRIM",
        order: 0,
        steps: [{ duration: durationDays, temperature: setpoint, order: 0 }],
      },
    ],
  };
};

/**
 * BrewTimeline — Left sidebar showing all brew steps sequentially
 */
const BrewTimeline = ({ session }) => {
  const [recipe, setRecipe] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [timeline, setTimeline] = useState([]);

  // Fetch recipe when session is available
  useEffect(() => {
    if (session) {
      if (session.beer_recipe && typeof session.beer_recipe === "object") {
        setRecipe(session.beer_recipe);
      } else if (session.beer_recipe_id) {
        recipeService
          .getRecipeById(session.beer_recipe_id)
          .then((response) => {
            if (
              response &&
              (response.fermenting || response.mashing || response.boiling)
            ) {
              setRecipe(response);
            } else {
              setRecipe(createFallbackRecipe(session));
            }
          })
          .catch(() => {
            setRecipe(createFallbackRecipe(session));
          });
      } else if (session.fermentation_settings) {
        const settings = session.fermentation_settings;
        const steps = (settings.steps || []).map((step, idx) => ({
          duration: (step.duration_minutes || 0) / (24 * 60), // duration in days
          temperature: step.temperature,
          order: idx,
        }));
        setRecipe({
          beer_name:
            (session.beer && session.beer.name) || "Custom Fermentation",
          serving_temperature: settings.serving_temperature || 5.0,
          fermenting: [
            {
              fermentation_stage_type: "PRIM",
              order: 0,
              steps:
                steps.length > 0
                  ? steps
                  : [{ duration: 7, temperature: 18.0, order: 0 }],
            },
          ],
        });
      } else {
        setRecipe(createFallbackRecipe(session));
      }
    }
  }, [session && (session.beer_recipe_id || session.id)]);

  // Subscribe to MQTT frequent logs for live status
  useEffect(() => {
    let subscription;
    if (session) {
      subscription = mqttService.frequentLog$.subscribe((frequentlog) => {
        if (frequentlog.session === 0 || frequentlog.session === session.id) {
          const log = frequentlog.logsList[0];
          if (log && log.currentStatus) {
            setCurrentStatus({
              procState: log.currentStatus.procState,
              procType: log.currentStatus.procType,
              currentStepIndex: log.currentStatus.currentStepIndex,
              currentTempStepIndex: log.currentStatus.currentTempStepIndex,
              processTimeRemaining: log.processTimeRemaining,
              processTime: log.processTime,
              setpointTemp: log.setpointTemp,
              currentTemp: log.currentTemp,
              nextUserAction: log.nextUserAction || log.currentStatus.action,
            });
          }
        }
      });
    }
    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [session]);

  // Generate timeline when data changes
  useEffect(() => {
    if (session && recipe) {
      const isInactive =
        session.status === SESSION_STATES.STATUS_FINISHED ||
        session.status === SESSION_STATES.STATUS_ABORTED ||
        session.status === SESSION_STATES.STATUS_FAILED;

      // For inactive sessions, bypass the hardware device's current state and use furthest_status.
      // For active sessions, use device state from HTTP response as initial fallback if MQTT hasn't published yet.
      const status =
        currentStatus ||
        (!isInactive &&
        session.device &&
        (session.device.process_state > 0 || session.device.process_type > 0)
          ? {
              procState: session.device.process_state,
              procType: session.device.process_type,
              currentStepIndex: session.device.current_step_index,
              currentTempStepIndex: session.device.current_temp_step_index,
              setpointTemp: session.device.setpoint_temp,
              currentTemp: session.device.current_temp,
            }
          : session.furthest_status || null);

      const stages = generateTimeline(session, recipe, status);
      setTimeline(stages);
    }
  }, [session, recipe, currentStatus]);

  if (!session) return null;

  // Don't show for cleaning sessions
  if (
    session.session_type === 2 ||
    session.session_type === 3 ||
    session.session_type === 4
  ) {
    return null;
  }

  if (timeline.length === 0) {
    return (
      <div className="brew-timeline">
        <div className="brew-timeline__empty">Loading timeline…</div>
      </div>
    );
  }

  const activeStageId = timeline.find((s) =>
    s.entries.some((e) => e.status === "active" || e.status === "warning"),
  );

  // Fallback: use session.device.process_type before MQTT data arrives
  const getFallbackStageId = () => {
    if (!session || !session.device) return timeline[0];
    const pt = session.device.process_type;
    if (pt === 1)
      return timeline.find((s) => s.stage === "MASHING") || timeline[0];
    if (pt === 4)
      return timeline.find((s) => s.stage === "FERMENTATION") || timeline[0];
    if (pt === 5)
      return timeline.find((s) => s.stage === "SERVING") || timeline[0];
    return timeline[0];
  };

  // On mobile screens (< 992px), start with all stages collapsed by default
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 992;
  const expandedStage = isMobile ? null : activeStageId || getFallbackStageId();

  const isInactive =
    session &&
    (session.status === SESSION_STATES.STATUS_FINISHED ||
      session.status === SESSION_STATES.STATUS_ABORTED ||
      session.status === SESSION_STATES.STATUS_FAILED);

  return (
    <div className="brew-timeline">
      {timeline.map((stage) => (
        <TimelineStage
          key={stage.id}
          stage={stage}
          defaultOpen={expandedStage ? stage.id === expandedStage.id : false}
          isSessionInactive={isInactive}
        />
      ))}
    </div>
  );
};

BrewTimeline.propTypes = {
  session: PropTypes.object,
};

export default BrewTimeline;
